import { useEffect, useRef } from 'react';
import type { ChangeEvent, ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { settingsProfileUpdateInput } from '@xdocs/schemas';
import type { SettingsProfileUpdateInput } from '@xdocs/schemas';
import { assetToast } from '@xdocs/i18n';
import { Avatar, Badge, Button, EmptyState, Input, Skeleton, cn, useI18n, useToast } from '@xdocs/ui';
import { applyWsAlert } from '../../lib/applyWsAlert.js';
import { optionalProp } from '../../lib/optionalProp.js';
import { safeFieldError } from '../../i18n/safeT.js';
import { useAvatarUpload } from './useAvatarUpload.js';
import { useSettingsProfile } from './useSettingsProfile.js';
import { useSettingsT } from './useSettingsT.js';
import styles from './ProfileSection.module.css';

/** F10 Profile section: display name + avatar upload + read-only email/
 * auth-methods. Composed inside `SettingsPage` (`/settings`). */
export function ProfileSection(): ReactElement {
  const { locale } = useI18n();
  const { t } = useSettingsT();
  const toast = useToast();
  const { profile, isLoading, isError, onRetry, save, isSaving } = useSettingsProfile();
  const avatarUpload = useAvatarUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isDirty },
  } = useForm<SettingsProfileUpdateInput>({
    resolver: zodResolver(settingsProfileUpdateInput),
    defaultValues: { displayName: '' },
  });

  useEffect(() => {
    if (profile) reset({ displayName: profile.displayName ?? '' });
  }, [profile, reset]);

  function handleAvatarPick(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) void avatarUpload.upload(file);
  }

  async function onSubmit(values: SettingsProfileUpdateInput): Promise<void> {
    try {
      const payload: SettingsProfileUpdateInput = { ...values };
      if (avatarUpload.status === 'ready' && avatarUpload.fileId) payload.avatarFileId = avatarUpload.fileId;
      await save(payload);
      avatarUpload.reset();
      toast.push(assetToast(locale, 'success', t('settings:profile.toast.saved.action'), t('settings:profile.title')));
    } catch (err) {
      const banner = applyWsAlert(err, setError, locale);
      if (banner) toast.push({ tone: 'error', message: banner });
    }
  }

  async function handleRemoveAvatar(): Promise<void> {
    try {
      await save({ avatarFileId: null });
      avatarUpload.reset();
      toast.push(assetToast(locale, 'success', t('settings:profile.toast.saved.action'), t('settings:profile.title')));
    } catch {
      toast.push({ tone: 'error', message: t('settings:profile.toast.saveFailed') });
    }
  }

  if (isLoading) {
    return (
      <section aria-busy="true" className={cn(styles['card'])}>
        <Skeleton shape="tile" width="64px" height="64px" />
        <Skeleton shape="text-line" width="50%" />
        <Skeleton shape="text-line" width="70%" />
      </section>
    );
  }

  if (isError || !profile) {
    return (
      <EmptyState
        kind="error"
        title={t('settings:profile.status.error.title')}
        action={{ label: t('settings:action.retry'), onClick: onRetry }}
      />
    );
  }

  const avatarBusy = avatarUpload.status === 'uploading' || avatarUpload.status === 'processing';
  const displayName = profile.displayName ?? profile.email;

  return (
    <section className={cn(styles['card'])}>
      <h2 className={cn(styles['title'])}>{t('settings:profile.title')}</h2>

      <div className={cn(styles['avatarRow'])}>
        <Avatar name={displayName} loading={avatarUpload.status === 'uploading'} size={64} />
        <div className={cn(styles['avatarActions'])}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className={cn(styles['fileInput'])}
            onChange={handleAvatarPick}
            aria-label={t('settings:profile.avatar.upload')}
          />
          <Button
            variant="secondary"
            size="sm"
            state={avatarUpload.status === 'uploading' ? 'loading' : 'idle'}
            onClick={() => fileInputRef.current?.click()}
          >
            {t('settings:profile.avatar.upload')}
          </Button>
          {profile.avatarFileId && (
            <Button variant="ghost" size="sm" onClick={() => void handleRemoveAvatar()}>
              {t('settings:profile.avatar.remove')}
            </Button>
          )}
        </div>
      </div>
      {avatarUpload.status === 'processing' && <p className={cn(styles['hint'])}>{t('settings:profile.avatar.processing')}</p>}
      {avatarUpload.status === 'error' && (
        <p role="alert" className={cn(styles['error'])}>
          {t('settings:profile.avatar.error')}
        </p>
      )}

      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate className={cn(styles['form'])}>
        <Input
          label={t('settings:profile.displayName.label')}
          required
          {...optionalProp('error', safeFieldError(locale, errors.displayName?.message))}
          {...register('displayName')}
        />

        <div className={cn(styles['readonlyField'])}>
          <span className={cn(styles['readonlyLabel'])}>{t('settings:profile.email.label')}</span>
          <span className={cn(styles['readonlyValue'])}>
            {profile.email}{' '}
            <Badge tone={profile.emailVerifiedAt ? 'success' : 'warning'}>
              {profile.emailVerifiedAt ? t('settings:profile.email.verified') : t('settings:profile.email.unverified')}
            </Badge>
          </span>
        </div>

        <div className={cn(styles['readonlyField'])}>
          <span className={cn(styles['readonlyLabel'])}>{t('settings:profile.authMethods.label')}</span>
          <span className={cn(styles['authMethods'])}>
            {profile.authMethods.map((method) => (
              <Badge key={method} tone="neutral">
                {t(`settings:profile.authMethods.${method}`)}
              </Badge>
            ))}
          </span>
        </div>

        <div className={cn(styles['actions'])}>
          <Button
            variant="primary"
            type="submit"
            state={isSaving ? 'loading' : 'idle'}
            disabled={avatarBusy || (!isDirty && avatarUpload.status !== 'ready')}
            {...optionalProp('disabledReason', avatarBusy ? t('settings:profile.avatar.processing') : undefined)}
          >
            {t('settings:profile.action.save')}
          </Button>
        </div>
      </form>
    </section>
  );
}
